import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";

// Retour « réel » à la page précédente. Pour chaque entrée de l'historique,
// on retient l'entrée de l'application d'où l'on venait (sessionStorage : un
// rechargement de page ne l'oublie pas). « Retour » revient alors en arrière
// dans l'historique — la liste retrouve ses filtres (portés par l'URL) et sa
// position de défilement — et, sans page d'origine dans l'application (lien
// ouvert directement, nouvel onglet), va vers une destination logique.

const PARENTS = "bp.nav.parents";
const SCROLL = "bp.nav.scroll.";
const MAX_ENTRIES = 200;

function readParents(): Record<string, string> {
  try {
    return JSON.parse(sessionStorage.getItem(PARENTS) ?? "{}") as Record<string, string>;
  } catch {
    return {};
  }
}

function writeParents(parents: Record<string, string>): void {
  try {
    const keys = Object.keys(parents);
    for (const key of keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES))) delete parents[key];
    sessionStorage.setItem(PARENTS, JSON.stringify(parents));
  } catch {
    // Stockage indisponible (navigation privée stricte) : le repli s'applique.
  }
}

/** À appeler une seule fois, dans la coquille : mémorise la page d'origine de chaque entrée. */
export function useNavigationMemory(): void {
  const location = useLocation();
  const type = useNavigationType();
  const previous = useRef<string | null>(null);
  useEffect(() => {
    const prev = previous.current;
    previous.current = location.key;
    if (!prev || prev === location.key || type === "POP") return;
    const parents = readParents();
    if (type === "PUSH") parents[location.key] = prev;
    // Un remplacement (filtre d'une liste, redirection) garde l'origine de l'entrée remplacée.
    else if (parents[prev]) parents[location.key] = parents[prev]!;
    else return;
    writeParents(parents);
  }, [location.key, type]);
}

/** Vrai si la page courante a été ouverte depuis une autre page de l'application. */
export function useHasInAppPrevious(): boolean {
  const location = useLocation();
  return Boolean(readParents()[location.key]);
}

/**
 * Revenir à la page précédente de l'application, sinon aller à `fallback`
 * (remplace l'entrée : « Retour » n'empile pas une page de plus).
 */
export function useGoBack(fallback: string): () => void {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(() => {
    if (readParents()[location.key]) navigate(-1);
    else navigate(fallback, { replace: true });
  }, [navigate, location.key, fallback]);
}

/** Position de défilement retenue pour une entrée de l'historique. */
export function saveScroll(key: string, top: number): void {
  try {
    sessionStorage.setItem(SCROLL + key, String(Math.round(top)));
  } catch {
    // ignoré : la page s'ouvrira simplement en haut
  }
}

export function savedScroll(key: string): number {
  try {
    return Number(sessionStorage.getItem(SCROLL + key) ?? 0) || 0;
  } catch {
    return 0;
  }
}

/**
 * Rétablit une position de défilement quand le contenu (souvent chargé par
 * l'API) est assez haut pour l'atteindre ; abandonne si l'utilisateur fait
 * défiler lui-même ou après deux secondes.
 */
export function restoreScroll(el: HTMLElement, top: number): () => void {
  if (top <= 0) {
    el.scrollTo({ top: 0, left: 0, behavior: "instant" });
    return () => undefined;
  }
  let stopped = false;
  const started = performance.now();
  const stop = () => {
    stopped = true;
  };
  el.addEventListener("wheel", stop, { passive: true, once: true });
  el.addEventListener("touchstart", stop, { passive: true, once: true });
  const attempt = () => {
    if (stopped) return;
    el.scrollTo({ top, left: 0, behavior: "instant" });
    const reached = Math.abs(el.scrollTop - top) < 2;
    if (!reached && performance.now() - started < 2000) requestAnimationFrame(attempt);
  };
  requestAnimationFrame(attempt);
  return () => {
    stopped = true;
    el.removeEventListener("wheel", stop);
    el.removeEventListener("touchstart", stop);
  };
}
